import React from "react";
import format from "date-fns/format";
import { utcToZonedTime } from "date-fns-tz";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { globalStyles } from "@client/shared/constants";
import { noop } from "@client/shared/constants";
import { decodeToken, getToken } from "@client/utils";
import style from "./style";

const ConfirmDetailsView = ({
  broadcastTitle = "",
  recordingType = 1,
  recordedVoicemail = null,
  selectedVoicemail = {},
  callToRecordVoicemail = null,
  uploadedVoicemail = null,
  recipients = [],
  callerDetails = {},
  immediateRelease = false,
  releaseDate = "",
  notifyRecipient = false,
  bbc = [],
  includeSelfInBroadcast = false,
  timezone = null,
  handleChange = noop,
  showDetails = false,
  isNewForm = false,
  isFormChanged = false,
  broadcastComplete = false,
  broadcastErrors = [],
  isAllRecipients = false,
}) => {
  const geAudioSource = () => {
    const token = decodeToken(getToken());
    let audioSource = null;
    switch (+recordingType) {
      case 1:
        audioSource =
          recordedVoicemail &&
          (window.URL || window.webkitURL).createObjectURL(recordedVoicemail);
        break;
      case 2:
        audioSource =
          callToRecordVoicemail?.contentUri &&
          `${callToRecordVoicemail?.contentUri}?access_token=${token.rc_access_token}`;
        break;
      case 3:
        audioSource =
          selectedVoicemail?.contentUri &&
          `${selectedVoicemail?.contentUri}?access_token=${token.rc_access_token}`;
        break;
      case 4:
        audioSource =
          uploadedVoicemail &&
          (window.URL || window.webkitURL).createObjectURL(uploadedVoicemail);
        break;
    }

    return audioSource;
  };

  const modifiedBBC = bbc
    .map((item) => `${item.name} <${item?.contact?.email}>`)
    .join(", ");

  return (
    <Box
      sx={{
        ...globalStyles.display.flex,
        ...globalStyles.flex.direction.column,
        ...globalStyles.flex.justify.between,
        ...style.wrapper,
      }}
    >
      <Box>
        <Box
          sx={{
            ...globalStyles.display.flex,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              ...globalStyles.text.weight.bold,
              ...globalStyles.width["25"],
            }}
          >
            Campaign Name:
          </Typography>
          <Tooltip title={broadcastTitle} placement="top-start">
            <Typography
              variant="body2"
              noWrap
              sx={{ ...globalStyles.width["75"], ...style.campaignName }}
            >
              {broadcastTitle}
            </Typography>
          </Tooltip>
        </Box>
        <Box
          sx={{
            mt: 1,
            ...globalStyles.display.flex,
            ...globalStyles.flex.align.center,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              ...globalStyles.text.weight.bold,
              ...globalStyles.width["25"],
            }}
          >
            Audio Recording:
          </Typography>
          <Box
            sx={{
              ...style.audioWrapper,
              ...globalStyles.width["75"],
            }}
          >
            <Box
              sx={{
                ...style.audioPlayerWrapper,
              }}
            >
              {+recordingType === 3 && !selectedVoicemail?.contentUri ? (
                <Box
                  sx={{
                    ...style.audioExpired,
                    p: 0.8,
                    ...globalStyles.width["60"],
                  }}
                >
                  <Typography variant="body2">Audio File Expired</Typography>
                </Box>
              ) : (
                <audio controls src={geAudioSource()} />
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            mt: 1,
            ...globalStyles.display.flex,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              ...globalStyles.text.weight.bold,
              ...globalStyles.width["25"],
            }}
          >
            Recipients:
          </Typography>
          <Tooltip
            title={
              isAllRecipients
                ? "All Extensions"
                : recipients.map((item) => item.name).join(",") || ""
            }
            placement="top-start"
          >
            <Typography
              noWrap
              variant="body2"
              sx={{ ...globalStyles.width["75"] }}
            >
              {isAllRecipients
                ? "All Extensions"
                : recipients.map((item) => item.name).join(",")}
            </Typography>
          </Tooltip>
        </Box>
        <Box
          sx={{
            mt: 1,
            ...globalStyles.display.flex,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              ...globalStyles.text.weight.bold,
              ...globalStyles.width["25"],
            }}
          >
            Caller ID:
          </Typography>
          <Box
            sx={{
              ...globalStyles.width["75"],
              display: "flex",
              alignItems: "center",
            }}
          >
            <Tooltip
              title={callerDetails.callerIdName || "-"}
              placement="top-start"
            >
              <Typography
                variant="body2"
                noWrap={callerDetails.callerIdName.length > 45}
              >
                {callerDetails.callerIdName || "-"}
              </Typography>
            </Tooltip>
            <Typography variant="body2" sx={{ mx: 1 }}>|</Typography>
            <Typography variant="body2" sx={{ ...globalStyles.width["75"] }}>
              {callerDetails.callerIdNumber || "-"}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            mt: 1,
            ...globalStyles.display.flex,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              ...globalStyles.text.weight.bold,
              ...globalStyles.width["25"],
            }}
          >
            Release Date:
          </Typography>
          <Tooltip
            title={
              (isNewForm || isFormChanged) &&
              immediateRelease &&
              !broadcastComplete
                ? ""
                : releaseDate && timezone
                ? `${format(
                    utcToZonedTime(releaseDate, timezone),
                    "MM-dd-yyy hh:mm:ss a"
                  )} ${timezone}`
                : ""
            }
            placement="top-start"
          >
            <Typography variant="body2" sx={{ ...globalStyles.width["75"] }}>
              {immediateRelease &&
              !broadcastComplete &&
              (isNewForm || isFormChanged)
                ? "Immediate Release"
                : releaseDate && timezone
                ? format(
                    utcToZonedTime(releaseDate, timezone),
                    "MM-dd-yyy hh:mm:ss a"
                  )
                : "-"}
            </Typography>
          </Tooltip>
        </Box>
        <Box
          sx={{
            mt: 1,
            ...globalStyles.display.flex,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              ...globalStyles.text.weight.bold,
              ...globalStyles.width["25"],
            }}
          >
            Notifications:
          </Typography>
          <Typography variant="body2" sx={{ ...globalStyles.width["75"] }}>
            {notifyRecipient ? "On" : "Off"}
          </Typography>
        </Box>
        <Box
          sx={{
            mt: 1,
            ...globalStyles.display.flex,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              ...globalStyles.text.weight.bold,
              ...globalStyles.width["25"],
            }}
          >
            BCC:
          </Typography>
          <Tooltip title={modifiedBBC || ""} placement="top-start">
            <Typography
              noWrap
              variant="body2"
              sx={{ ...globalStyles.width["75"] }}
            >
              {modifiedBBC}
            </Typography>
          </Tooltip>
        </Box>
      </Box>
      <Box
        sx={{
          ...globalStyles.display.flex,
          ...globalStyles.flex.justify.end,
        }}
      >
        <FormControlLabel
          labelPlacement="start"
          label={
            <Typography variant="body2">
              Include yourself in the broadcast:
            </Typography>
          }
          control={
            <Checkbox
              disabled={showDetails || broadcastComplete}
              checked={includeSelfInBroadcast}
              onChange={(evt) => {
                handleChange(
                  {
                    name: "includeSelfInBroadcast",
                    value: evt.target.checked,
                  },
                  "custome"
                );
              }}
            />
          }
        />
      </Box>
      {(showDetails || broadcastComplete) && !!broadcastErrors.length && (
        <Accordion
          elevation={0}
          sx={{
            ...style.errorAccordion,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="error" />}
            sx={{
              pl: 0,
              pr: 4,
              ...style.errorAccordionSummary,
              ...globalStyles.flex.justify.start,
            }}
          >
            <Typography
              variant="body1"
              color="error"
              sx={{
                mr: 1,
              }}
            >
              Issues Encountered
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Box
              sx={{
                p: 2,
                ...style.errorMessageWrapper,
              }}
            >
              {[
                {
                  type: "title",
                  message: "Issue Encountered",
                  extension: "Extension",
                },
                ...broadcastErrors,
              ].map((error) => {
                const isTitleType = error.type === "title";
                return (
                  <Box
                    sx={{
                      ...globalStyles.display.flex,
                      ...globalStyles.flex.justify.between,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color={isTitleType ? "default" : "error"}
                      sx={{
                        ml: 1,
                        ...(isTitleType ? globalStyles.text.weight.bold : {}),
                      }}
                    >
                      {error.message}
                    </Typography>
                    {error.extension && (
                      <Typography
                        variant="body2"
                        color={isTitleType ? "default" : "error"}
                        sx={{
                          mr: 1,
                          ...(isTitleType ? globalStyles.text.weight.bold : {}),
                        }}
                      >
                        {error.extension}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};

export default ConfirmDetailsView;
